export const getSetupCampaignsStatus = ({
  selectedIds = [],
  allCampaignConfigs = []
}) => {
  if (selectedIds.length === 0) {
    return false;
  }
  return selectedIds.some(selectedId => {
    const campaignConfigFineded = allCampaignConfigs.find(
      campaignConfig => campaignConfig.id === selectedId
    );
    if (campaignConfigFineded) {
      const { setup = [] } = campaignConfigFineded;
      if (setup.length > 0) {
        return !setup.some(el =>
          ['done', 'error', 'aborted'].includes(el.status)
        );
      }
      return false;
    }
    return true;
  });
};

export const SETUP_IN_PROGRESS_STATUSES = [
  'queued',
  'toDo',
  'uploadingSegments',
  'uploadInProgress',
  'segmentsExported',
  'uploadingCreatives',
  'creativesUploaded',
  'SettingUpCampaign',
  'creatingOrder',
  'orderCreated',
  'creatingLineItems',
  'lineItemsCreated',
  'attachingAds'
];

export const PENDING_UPSERT_STATUSES = ['pending', 'inProgress', 'error'];

export const isUpsertLastAction = (
  lastUpsertTask,
  setupTask,
  setupStatus = setupTask?.status
) => {
  if (lastUpsertTask?.status !== 'pending' || !lastUpsertTask?.requestedAt) {
    return false;
  }
  if (SETUP_IN_PROGRESS_STATUSES.includes(setupStatus)) {
    return false;
  }
  if (!setupTask) {
    return true;
  }
  return new Date(lastUpsertTask.requestedAt) > new Date(setupTask.updatedAt);
};

const getGeoZoneLabel = ({ audienceType, parameters }) => {
  const codes = Object.values(parameters).flat();
  if (codes.length === 0) {
    return null;
  }
  const firstCode = codes[0]?.key || codes[0] || '';
  const displayCount = String(firstCode).length <= 5 ? 2 : 1;
  const displayedCodes = codes
    .slice(0, displayCount)
    .map(code => code?.key || code)
    .join(' / ');
  const remainingCount = codes.length - displayCount;

  return remainingCount > 0
    ? `${audienceType} : ${displayedCodes} + ${remainingCount} autre${
        remainingCount > 1 ? 's' : ''
      }`
    : `${audienceType} : ${displayedCodes}`;
};

const formatDecimal = value => String(Number(parseFloat(value).toFixed(2)));

const formatRadius = ({ distance, unit }) => {
  const value = parseFloat(distance);
  if (Number.isNaN(value)) {
    return null;
  }
  if (unit === 'm' && value >= 1000) {
    return `Rayon ${formatDecimal(value / 1000)} km`;
  }
  return `Rayon ${formatDecimal(value)} ${unit}`;
};

export const getInsightShopLabel = shop => {
  if (!shop?.parameters) {
    return null;
  }
  const { audienceType, parameters } = shop;

  if (audienceType === 'radius') {
    return formatRadius(parameters);
  }
  if (audienceType === 'isochrony') {
    return `Isochronie ${formatDecimal(parameters.time)} min. ${
      parameters.transportation
    }`;
  }
  return getGeoZoneLabel(shop);
};

export const getInsightShopsLabel = (shops = []) => {
  const labels = shops.reduce((acc, shop) => {
    const label = getInsightShopLabel(shop);
    if (label && !acc.includes(label)) {
      acc.push(label);
    }
    return acc;
  }, []);

  return labels.length > 0 ? labels.join(' · ') : null;
};

const getGeoZoneKeys = filter => {
  if (!filter) {
    return [];
  }
  if (Array.isArray(filter)) {
    return filter.flatMap(item => getGeoZoneKeys(item));
  }
  if (filter.value) {
    return getGeoZoneKeys(filter.value);
  }
  if (filter.filters) {
    return getGeoZoneKeys(filter.filters);
  }
  return filter.criteriaKey && !filter.insightShop ? [filter.criteriaKey] : [];
};

const countGeoZones = filter => getGeoZoneKeys(filter).length;

export const getGeoTargetingSignature = (filter, shops = []) => {
  if (shops.length > 0) {
    return shops
      .map(
        shop =>
          `${shop.key || shop.name || shop._id}|${getInsightShopLabel(shop)}`
      )
      .sort()
      .join('#');
  }
  return getGeoZoneKeys(filter)
    .sort()
    .join('#');
};

export const getGeoTargetingLabel = (filter, shops = []) => {
  const shopsLabel = getInsightShopsLabel(shops);
  if (shopsLabel) {
    return shopsLabel;
  }
  const zonesCount = countGeoZones(filter);

  return zonesCount > 0
    ? `${zonesCount} zone${zonesCount > 1 ? 's' : ''}`
    : null;
};

export const getLabel = (mediaOrder, item) => {
  if (!mediaOrder?.objective) {
    return null;
  }
  // eslint-disable-next-line prefer-destructuring
  const objectiveLabel = item?.objectives?.find(
    objective => mediaOrder.objective === objective.objectiveKey
  )?.objectiveLabel;

  let subObjectiveLabel = '';
  if (mediaOrder?.subObjective) {
    // eslint-disable-next-line prefer-destructuring
    subObjectiveLabel = item?.objectives
      ?.find(objective => mediaOrder.objective === objective.objectiveKey)
      ?.subObjectives.find(
        subObjective => subObjective.subObjectiveKey === mediaOrder.subObjective
      )?.subObjectiveLabel;
  }

  return subObjectiveLabel
    ? `${objectiveLabel} - ${subObjectiveLabel}`
    : `${objectiveLabel}`;
};

export const getConversionLocationsForSubObjective = (
  objective,
  subObjectiveKey
) =>
  objective?.conversionLocations?.filter(location =>
    location?.locationSubObjectives?.includes(subObjectiveKey)
  ) ?? [];

export const getConversionLocationLabel = (objective, locationKey) =>
  objective?.conversionLocations?.find(
    location => location.locationKey === locationKey
  )?.locationLabel ?? locationKey;

export const enrichConversionLocations = (matchingObjective, objective) =>
  (matchingObjective?.conversionLocations || []).map(apiLocation => ({
    ...apiLocation,
    locationLabel: getConversionLocationLabel(
      objective,
      apiLocation.locationKey
    )
  }));

const vswConversionLocations = [
  { locationKey: 'WEBSITE', locationLabel: 'Site web' },
  {
    locationKey: 'INSTAGRAM_PROFILE_AND_FACEBOOK_PAGE',
    locationLabel: 'Instagram ou Facebook'
  },
  { locationKey: 'PHONE_CALL', locationLabel: 'Appels' },
  {
    locationKey: 'MESSENGER',
    locationLabel: 'Messenger'
  }
];

const liConversionLocations = [
  { locationKey: 'WEBSITE', locationLabel: 'Site web' },
  { locationKey: 'ON_AD', locationLabel: 'Formulaire instantané' },
  {
    locationKey: 'MESSENGER',
    locationLabel: 'Messenger'
  },
  {
    locationKey: 'LEAD_FROM_IG_DIRECT',
    locationLabel: 'Instagram'
  },
  { locationKey: 'PHONE_CALL', locationLabel: 'Appels' }
];

const vlConversionLocations = [
  { locationKey: 'WEBSITE', locationLabel: 'Site web' },
  { locationKey: 'PHONE_CALL', locationLabel: 'Appels' }
];

const intConversionLocations = [
  { locationKey: 'ON_VIDEO', locationLabel: 'Sur votre publicité' },
  {
    locationKey: 'ON_POST',
    locationLabel: 'Sur votre publicité (Interactions avec la publication)'
  },
  {
    locationKey: 'ON_EVENT',
    locationLabel: 'Sur votre publicité (Réponses aux évènements)'
  },
  { locationKey: 'WEBSITE', locationLabel: 'Site web' },
  { locationKey: 'PHONE_CALL', locationLabel: 'Appels' },
  { locationKey: 'ON_PAGE', locationLabel: 'Instagram ou Facebook' }
];

export const mockedObjectives = {
  platforms: [
    {
      platform: 'facebook',
      platforms: ['facebook'],
      platformLabel: 'Facebook',
      objectives: [
        {
          objectiveLabel: 'Ventes',
          objectiveKey: 'vl',
          conversionLocations: vlConversionLocations,
          subObjectives: [
            {
              subObjectiveLabel:
                'Maximiser le nombre de vues de la page de destination',
              subObjectiveKey: 'LANDING_PAGE_VIEWS'
            },
            {
              subObjectiveLabel: 'Maximiser le nombre de clics sur un lien',
              subObjectiveKey: 'LINK_CLICKS'
            },
            {
              subObjectiveLabel: "Maximiser le nombre d'impressions",
              subObjectiveKey: 'IMPRESSIONS'
            },
            {
              subObjectiveLabel: 'Maximiser la couverture unique quotidienne',
              subObjectiveKey: 'REACH'
            },
            {
              subObjectiveLabel: "Maximiser le nombre d'appels",
              subObjectiveKey: 'QUALITY_CALL'
            },
            {
              subObjectiveLabel: 'Maximiser le nombre de conversions',
              subObjectiveKey: 'OFFSITE_CONVERSIONS'
            },
            {
              subObjectiveLabel: 'Maximiser la valeur des conversions',
              subObjectiveKey: 'VALUE'
            }
          ]
        },
        {
          objectiveLabel: 'Prospects',
          objectiveKey: 'li',
          conversionLocations: liConversionLocations,
          subObjectives: [
            {
              subObjectiveLabel:
                'Maximiser le nombre de vues de la page de destination',
              subObjectiveKey: 'LANDING_PAGE_VIEWS'
            },
            {
              subObjectiveLabel: 'Maximiser le nombre de clics sur un lien',
              subObjectiveKey: 'LINK_CLICKS'
            },
            {
              subObjectiveLabel: "Maximiser le nombre d'impressions",
              subObjectiveKey: 'IMPRESSIONS'
            },
            {
              subObjectiveLabel: 'Maximiser la couverture unique quotidienne',
              subObjectiveKey: 'REACH'
            },
            {
              subObjectiveLabel: "Maximiser le nombre d'appels",
              subObjectiveKey: 'QUALITY_CALL'
            },
            {
              subObjectiveLabel: 'Maximiser le nombre de conversions',
              subObjectiveKey: 'OFFSITE_CONVERSIONS'
            },
            {
              subObjectiveLabel: 'Maximiser le nombre de prospects',
              subObjectiveKey: 'LEAD_GENERATION'
            },
            {
              subObjectiveLabel:
                'Maximiser le nombre de prospects de conversions',
              subObjectiveKey: 'QUALITY_LEAD'
            }
          ]
        },
        {
          objectiveLabel: 'Trafic',
          objectiveKey: 'vsw',
          conversionLocations: vswConversionLocations,
          subObjectives: [
            {
              subObjectiveLabel:
                'Maximiser le nombre de vues de la page de destination',
              subObjectiveKey: 'LANDING_PAGE_VIEWS'
            },
            {
              subObjectiveLabel: 'Maximiser le nombre de clics sur un lien',
              subObjectiveKey: 'LINK_CLICKS'
            },
            {
              subObjectiveLabel: "Maximiser le nombre d'impressions",
              subObjectiveKey: 'IMPRESSIONS'
            },
            {
              subObjectiveLabel: 'Maximiser la couverture unique quotidienne',
              subObjectiveKey: 'REACH'
            },
            {
              subObjectiveLabel: 'Maximiser le nombre de conversations',
              subObjectiveKey: 'CONVERSATIONS'
            },
            {
              subObjectiveLabel:
                'Maximiser le nombre de visites du profil Instagram',
              subObjectiveKey: 'VISIT_INSTAGRAM_PROFILE'
            },
            {
              subObjectiveLabel: "Maximiser le nombre d'appels",
              subObjectiveKey: 'QUALITY_CALL'
            }
          ]
        },
        {
          objectiveLabel: 'Notoriété',
          objectiveKey: 'noto',
          subObjectives: [
            {
              subObjectiveLabel: 'Maximiser la couverture des publicités',
              subObjectiveKey: 'REACH'
            },
            {
              subObjectiveLabel: 'Maximiser les vues ThruPlay',
              subObjectiveKey: 'THRUPLAY'
            },
            {
              subObjectiveLabel:
                'Maximiser les vues de vidéos de 2 secondes consécutives',
              subObjectiveKey: 'TWO_SECOND_CONTINUOUS_VIDEO_VIEWS'
            },
            {
              subObjectiveLabel: "Maximiser le nombre d'impressions",
              subObjectiveKey: 'IMPRESSIONS'
            },
            {
              subObjectiveLabel:
                "Maximiser l'augmentation de la mémorisation publicitaire",
              subObjectiveKey: 'AD_RECALL_LIFT'
            }
          ]
        },
        {
          objectiveLabel: 'Interactions',
          objectiveKey: 'int',
          conversionLocations: intConversionLocations,
          subObjectives: [
            {
              subObjectiveLabel: 'Maximiser les vues ThruPlay',
              subObjectiveKey: 'THRUPLAY'
            },
            {
              subObjectiveLabel:
                'Maximiser les vues de vidéos de 2 secondes consécutives',
              subObjectiveKey: 'TWO_SECOND_CONTINUOUS_VIDEO_VIEWS'
            },
            {
              subObjectiveLabel:
                'Maximiser les interactions grâce à une publication',
              subObjectiveKey: 'POST_ENGAGEMENT'
            },
            {
              subObjectiveLabel: 'Maximiser la couverture unique quotidienne',
              subObjectiveKey: 'REACH'
            },
            {
              subObjectiveLabel: "Maximiser le nombre d'impressions",
              subObjectiveKey: 'IMPRESSIONS'
            },
            {
              subObjectiveLabel:
                'Maximiser le nombre de réponses à un évènement',
              subObjectiveKey: 'EVENT_RESPONSES'
            },
            {
              subObjectiveLabel:
                'Maximiser le nombre de vues de la page de destination',
              subObjectiveKey: 'LANDING_PAGE_VIEWS'
            },
            {
              subObjectiveLabel: 'Maximiser le nombre de clics sur un lien',
              subObjectiveKey: 'LINK_CLICKS'
            },
            {
              subObjectiveLabel: "Maximiser le nombre d'appels",
              subObjectiveKey: 'QUALITY_CALL'
            },
            {
              subObjectiveLabel:
                "Maximiser le nombre de mentions J'aime la Page",
              subObjectiveKey: 'PAGE_LIKES'
            },
            {
              subObjectiveLabel: 'Maximiser le nombre de conversions',
              subObjectiveKey: 'OFFSITE_CONVERSIONS'
            }
          ]
        },
        {
          objectiveLabel: 'Ventes',
          objectiveKey: 'vo',
          subObjectives: []
        },
        {
          objectiveLabel: 'Ventes',
          objectiveKey: 'vm',
          subObjectives: []
        },
        {
          objectiveLabel: 'Visites en magasin',
          objectiveKey: 'vem',
          subObjectives: []
        }
      ]
    },
    {
      platform: 'instagram',
      platforms: ['instagram'],
      platformLabel: 'Instagram',
      objectives: [
        {
          objectiveLabel: 'Ventes',
          objectiveKey: 'vl',
          conversionLocations: vlConversionLocations,
          subObjectives: [
            {
              subObjectiveLabel:
                'Maximiser le nombre de vues de la page de destination',
              subObjectiveKey: 'LANDING_PAGE_VIEWS'
            },
            {
              subObjectiveLabel: 'Maximiser le nombre de clics sur un lien',
              subObjectiveKey: 'LINK_CLICKS'
            },
            {
              subObjectiveLabel: "Maximiser le nombre d'impressions",
              subObjectiveKey: 'IMPRESSIONS'
            },
            {
              subObjectiveLabel: 'Maximiser la portée',
              subObjectiveKey: 'REACH'
            },
            {
              subObjectiveLabel: "Maximiser le nombre d'appels",
              subObjectiveKey: 'QUALITY_CALL'
            },
            {
              subObjectiveLabel: 'Maximiser le nombre de conversions',
              subObjectiveKey: 'OFFSITE_CONVERSIONS'
            },
            {
              subObjectiveLabel:
                'Maximiser le retour sur les dépenses publicitaires',
              subObjectiveKey: 'VALUE'
            }
          ]
        },
        {
          objectiveLabel: 'Prospects',
          objectiveKey: 'li',
          conversionLocations: liConversionLocations,
          subObjectives: [
            {
              subObjectiveLabel:
                'Maximiser le nombre de vues de la page de destination',
              subObjectiveKey: 'LANDING_PAGE_VIEWS'
            },
            {
              subObjectiveLabel: 'Maximiser le nombre de clics sur un lien',
              subObjectiveKey: 'LINK_CLICKS'
            },
            {
              subObjectiveLabel: "Maximiser le nombre d'impressions",
              subObjectiveKey: 'IMPRESSIONS'
            },
            {
              subObjectiveLabel: 'Maximiser la portée',
              subObjectiveKey: 'REACH'
            },
            {
              subObjectiveLabel: "Maximiser le nombre d'appels",
              subObjectiveKey: 'QUALITY_CALL'
            },
            {
              subObjectiveLabel: 'Maximiser le nombre de conversions',
              subObjectiveKey: 'OFFSITE_CONVERSIONS'
            },
            {
              subObjectiveLabel: 'Maximiser le nombre de prospects',
              subObjectiveKey: 'LEAD_GENERATION'
            },
            {
              subObjectiveLabel: 'Maximiser le nombre de prospects de qualité',
              subObjectiveKey: 'QUALITY_LEAD'
            }
          ]
        },
        {
          objectiveLabel: 'Trafic',
          objectiveKey: 'vsw',
          conversionLocations: vswConversionLocations,
          subObjectives: [
            {
              subObjectiveLabel:
                'Maximiser le nombre de vues de la page de destination',
              subObjectiveKey: 'LANDING_PAGE_VIEWS'
            },
            {
              subObjectiveLabel: 'Maximiser le nombre de clics sur un lien',
              subObjectiveKey: 'LINK_CLICKS'
            },
            {
              subObjectiveLabel: "Maximiser le nombre d'impressions",
              subObjectiveKey: 'IMPRESSIONS'
            },
            {
              subObjectiveLabel: 'Maximiser la portée',
              subObjectiveKey: 'REACH'
            },
            {
              subObjectiveLabel: 'Maximiser le nombre de conversations',
              subObjectiveKey: 'CONVERSATIONS'
            },
            {
              subObjectiveLabel:
                'Maximiser le nombre de visites du profil Instagram',
              subObjectiveKey: 'VISIT_INSTAGRAM_PROFILE'
            },
            {
              subObjectiveLabel: "Maximiser le nombre d'appels",
              subObjectiveKey: 'QUALITY_CALL'
            }
          ]
        },
        {
          objectiveLabel: 'Notoriété',
          objectiveKey: 'noto',
          subObjectives: [
            {
              subObjectiveLabel: 'Maximiser la couverture des publicités',
              subObjectiveKey: 'REACH'
            },
            {
              subObjectiveLabel: 'Maximiser les vues ThruPlay',
              subObjectiveKey: 'THRUPLAY'
            },
            {
              subObjectiveLabel:
                'Maximiser les vues de vidéos de 2 secondes consécutives',
              subObjectiveKey: 'TWO_SECOND_CONTINUOUS_VIDEO_VIEWS'
            },
            {
              subObjectiveLabel: "Maximiser le nombre d'impressions",
              subObjectiveKey: 'IMPRESSIONS'
            },
            {
              subObjectiveLabel:
                "Maximiser l'augmentation de la mémorisation publicitaire",
              subObjectiveKey: 'AD_RECALL_LIFT'
            }
          ]
        },
        {
          objectiveLabel: 'Interactions',
          objectiveKey: 'int',
          conversionLocations: intConversionLocations,
          subObjectives: [
            {
              subObjectiveLabel: 'Maximiser les vues ThruPlay',
              subObjectiveKey: 'THRUPLAY'
            },
            {
              subObjectiveLabel:
                'Maximiser les vues de vidéos de 2 secondes consécutives',
              subObjectiveKey: 'TWO_SECOND_CONTINUOUS_VIDEO_VIEWS'
            },
            {
              subObjectiveLabel:
                'Maximiser les interactions grâce à une publication',
              subObjectiveKey: 'POST_ENGAGEMENT'
            },
            {
              subObjectiveLabel: 'Maximiser la couverture unique quotidienne',
              subObjectiveKey: 'REACH'
            },
            {
              subObjectiveLabel: "Maximiser le nombre d'impressions",
              subObjectiveKey: 'IMPRESSIONS'
            },
            {
              subObjectiveLabel:
                'Maximiser le nombre de réponses à un évènement',
              subObjectiveKey: 'EVENT_RESPONSES'
            },
            {
              subObjectiveLabel:
                'Maximiser le nombre de vues de la page de destination',
              subObjectiveKey: 'LANDING_PAGE_VIEWS'
            },
            {
              subObjectiveLabel: 'Maximiser le nombre de clics sur un lien',
              subObjectiveKey: 'LINK_CLICKS'
            },
            {
              subObjectiveLabel: "Maximiser le nombre d'appels",
              subObjectiveKey: 'QUALITY_CALL'
            },
            {
              subObjectiveLabel:
                "Maximiser le nombre de mentions J'aime la Page",
              subObjectiveKey: 'PAGE_LIKES'
            },
            {
              subObjectiveLabel: 'Maximiser le nombre de conversions',
              subObjectiveKey: 'OFFSITE_CONVERSIONS'
            }
          ]
        },
        {
          objectiveLabel: 'Ventes',
          objectiveKey: 'vo',
          subObjectives: []
        },
        {
          objectiveLabel: 'Ventes',
          objectiveKey: 'vm',
          subObjectives: []
        },
        {
          objectiveLabel: 'Visites en magasin',
          objectiveKey: 'vem',
          subObjectives: []
        }
      ]
    },
    {
      platform: 'dv360',
      platforms: [
        'dv360',
        'leboncoin',
        'argus',
        'aVendreALouer',
        'agriaffaires',
        'machineryzone',
        'truckcorners'
      ],
      platformLabel: 'DV360',
      objectives: [
        {
          objectiveLabel: 'Visites sur site web',
          objectiveKey: 'vsw',
          subObjectives: [
            {
              subObjectiveLabel: 'Générer des visites ou des actions en ligne',
              subObjectiveKey: 'CAMPAIGN_GOAL_TYPE_ONLINE_ACTION'
            },
            {
              subObjectiveLabel:
                "Renforcer la notoriété d'une marque ou d'un produit",
              subObjectiveKey: 'CAMPAIGN_GOAL_TYPE_BRAND_AWARENESS'
            }
          ]
        },
        {
          objectiveLabel: 'Downloads application mobile',
          objectiveKey: 'dam',
          subObjectives: [
            {
              subObjectiveLabel:
                "Générer des installations d'applications ou des engagements",
              subObjectiveKey: 'CAMPAIGN_GOAL_TYPE_APP_INSTALL'
            }
          ]
        },
        {
          objectiveLabel: 'Ventes en ligne',
          objectiveKey: 'vl',
          subObjectives: [
            {
              subObjectiveLabel: 'Générer des visites ou des actions en ligne',
              subObjectiveKey: 'CAMPAIGN_GOAL_TYPE_ONLINE_ACTION'
            },
            {
              subObjectiveLabel:
                "Renforcer la notoriété d'une marque ou d'un produit",
              subObjectiveKey: 'CAMPAIGN_GOAL_TYPE_BRAND_AWARENESS'
            }
          ]
        },
        {
          objectiveLabel: 'Ventes en magasin',
          objectiveKey: 'vm',
          subObjectives: [
            {
              subObjectiveLabel:
                'Générez des ventes hors connexion ou en magasin.',
              subObjectiveKey: 'CAMPAIGN_GOAL_TYPE_OFFLINE_ACTION'
            }
          ]
        },
        {
          objectiveLabel: 'Ventes omnicanales',
          objectiveKey: 'vo',
          subObjectives: [
            {
              subObjectiveLabel: 'Générer des visites ou des actions en ligne',
              subObjectiveKey: 'CAMPAIGN_GOAL_TYPE_ONLINE_ACTION'
            }
          ]
        },
        {
          objectiveLabel: 'Visites en magasin',
          objectiveKey: 'vem',
          subObjectives: [
            {
              subObjectiveLabel:
                'Générez des ventes hors connexion ou en magasin.',
              subObjectiveKey: 'CAMPAIGN_GOAL_TYPE_OFFLINE_ACTION'
            }
          ]
        },
        {
          objectiveLabel: 'Leads ou inscrits',
          objectiveKey: 'li',
          subObjectives: [
            {
              subObjectiveLabel: 'Générer des visites ou des actions en ligne',
              subObjectiveKey: 'CAMPAIGN_GOAL_TYPE_ONLINE_ACTION'
            },
            {
              subObjectiveLabel:
                "Renforcer la notoriété d'une marque ou d'un produit",
              subObjectiveKey: 'CAMPAIGN_GOAL_TYPE_BRAND_AWARENESS'
            }
          ]
        },
        {
          objectiveLabel: 'Notoriété',
          objectiveKey: 'noto',
          subObjectives: [
            {
              subObjectiveLabel:
                "Renforcer la notoriété d'une marque ou d'un produit",
              subObjectiveKey: 'CAMPAIGN_GOAL_TYPE_BRAND_AWARENESS'
            }
          ]
        }
      ]
    }
    // TODO Uncomment when Taboola & GADS will be operationnal
    // {
    //   platform: 'taboola',
    //   platformLabel: 'Taboola',
    //   objectives: [
    //     {
    //       objectiveLabel: 'Lead generation',
    //       objectiveKey: 'lead-generation',
    //       subObjectives: []
    //     },
    //     {
    //       objectiveLabel: 'Online purchases',
    //       objectiveKey: 'online-purchases',
    //       subObjectives: []
    //     },
    //     {
    //       objectiveLabel: 'Website engagement',
    //       objectiveKey: 'website-engagement',
    //       subObjectives: []
    //     },
    //     {
    //       objectiveLabel: 'Brand awareness',
    //       objectiveKey: 'brand-awareness',
    //       subObjectives: []
    //     },
    //     {
    //       objectiveLabel: 'App promotion',
    //       objectiveKey: 'app-promotion',
    //       subObjectives: []
    //     }
    //   ]
    // },
    // {
    //   platform: 'googleAds',
    //   platformLabel: 'GADS',
    //   objectives: [
    //     {
    //       objectiveLabel: 'Ventes',
    //       objectiveKey: 'ventes',
    //       subObjectives: []
    //     },
    //     {
    //       objectiveLabel: 'Prospects',
    //       objectiveKey: 'prospects',
    //       subObjectives: []
    //     },
    //     {
    //       objectiveLabel: 'Trafic vers le site web',
    //       objectiveKey: 'trafic-vers-le-site-web',
    //       subObjectives: []
    //     }
    //   ]
    // }
  ]
};

export const getDataForCampaignUpdate = ({
  selectedIds = [],
  allCampaignConfigs = []
}) => {
  const allCampaignSelected = allCampaignConfigs.filter(campaignConfig =>
    selectedIds.includes(campaignConfig._id)
  );

  if (!allCampaignSelected.length) {
    return null;
  }

  const platforms = allCampaignSelected.reduce((acc, campaign) => {
    if (!Array.isArray(campaign?.platforms)) {
      return acc;
    }

    campaign.platforms.reduce((innerAcc, platform) => {
      if (platform) {
        innerAcc.push(platform);
      }
      return innerAcc;
    }, acc);

    return acc;
  }, []);

  const usedPlatforms = platforms.reduce((acc, platform) => {
    if (platform?.media) {
      acc.add(platform.media);
    }
    return acc;
  }, new Set());

  const mockedMedia = mockedObjectives.platforms.filter(obj =>
    obj.platforms.some(platform => usedPlatforms.has(platform))
  );

  const base = allCampaignSelected[0];

  const data = allCampaignSelected.reduce(
    (acc, campaignConfig) => {
      if (acc.start !== campaignConfig.start) {
        acc.start = null;
      }
      if (acc.end !== campaignConfig.end) {
        acc.end = null;
      }
      if (acc.status !== campaignConfig.status) {
        acc.status = null;
      }
      if (acc.paid !== campaignConfig.paid) {
        acc.paid = null;
      }
      return acc;
    },
    {
      start: base.start,
      end: base.end,
      status: base.status,
      paid: base.paid,
      platforms,
      mockedMedia,
      usedPlatforms: [...usedPlatforms]
    }
  );

  const allObjectivesEqual = allCampaignSelected.every(
    campaign => campaign.objective === base.objective
  );
  data.objective = allObjectivesEqual ? base.objective : null;
  return data;
};

export default getSetupCampaignsStatus;
